import { useMemo } from 'react';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import type { AccessCentrePermission } from 'domains/accessCentre/types';
import PermissionModuleCard from '../PermissionModuleCard';

const CARD_TITLE = 'Step 2: Assign Permissions';

const cardStyles = {
    width: '100%',
};

/** Group permissions by module name. */
const groupByModule = (permissions: AccessCentrePermission[]): Map<string, AccessCentrePermission[]> => {
    const map = new Map<string, AccessCentrePermission[]>();
    for (const p of permissions) {
        const list = map.get(p.module) ?? [];
        list.push(p);
        map.set(p.module, list);
    }
    return map;
};

/**
 * Returns all permission ids that are transitive dependencies of the given permission
 * (e.g. if C depends on B and B depends on A, returns [A, B] for C).
 */
const getTransitiveDependencyIds = (permissions: AccessCentrePermission[], permissionId: number): number[] => {
    const byKey = new Map(permissions.map((p) => [p.key, p]));
    const perm = permissions.find((p) => p.id === permissionId);
    if (!perm?.depends_on?.length) return [];
    const keysToAdd = new Set<string>();
    const queue = [...perm.depends_on];
    while (queue.length > 0) {
        const key = queue.shift()!;
        if (keysToAdd.has(key)) continue;
        keysToAdd.add(key);
        const p = byKey.get(key);
        if (p?.depends_on?.length) {
            for (const k of p.depends_on) {
                if (!keysToAdd.has(k)) queue.push(k);
            }
        }
    }
    return Array.from(keysToAdd)
        .map((key) => byKey.get(key)?.id)
        .filter((id): id is number => id !== undefined);
};

export type AssignPermissionsCardProps = {
    permissions: AccessCentrePermission[];
    selectedPermissionIds: number[];
    onSelectionChange: (permissionIds: number[]) => void;
    isLoading?: boolean;
    readOnly?: boolean;
};

const AssignPermissionsCard = ({
    permissions,
    selectedPermissionIds,
    onSelectionChange,
    isLoading = false,
    readOnly = false,
}: AssignPermissionsCardProps) => {
    const permissionsByModule = useMemo(() => groupByModule(permissions), [permissions]);
    const moduleKeys = useMemo(() => Array.from(permissionsByModule.keys()).sort(), [permissionsByModule]);

    const togglePermission = (id: number) => {
        const set = new Set(selectedPermissionIds);
        if (set.has(id)) {
            set.delete(id);
        } else {
            set.add(id);
            const dependencyIds = getTransitiveDependencyIds(permissions, id);
            for (const depId of dependencyIds) set.add(depId);
        }
        onSelectionChange(Array.from(set));
    };

    if (isLoading) {
        return (
            <Card variant="outlined" sx={cardStyles} data-testid="access-center-assign-permissions-card">
                <CardContent>
                    <Typography variant="h6" component="h2" gutterBottom>
                        {CARD_TITLE}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Loading permissions...
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card variant="outlined" sx={cardStyles} data-testid="access-center-assign-permissions-card">
            <CardContent>
                <Typography variant="h6" component="h2" gutterBottom>
                    {CARD_TITLE}
                </Typography>
                <Stack spacing={0} sx={{ mt: 1 }}>
                    {moduleKeys.map((moduleKey) => {
                        const modulePermissions = permissionsByModule.get(moduleKey) ?? [];
                        return (
                            <PermissionModuleCard
                                key={moduleKey}
                                moduleKey={moduleKey}
                                permissions={modulePermissions}
                                selectedPermissionIds={selectedPermissionIds}
                                onTogglePermission={togglePermission}
                                readOnly={readOnly}
                            />
                        );
                    })}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default AssignPermissionsCard;
